// Chat between people: the rules the worker and the web app share. A message is written in a small rich-text
// editor; the worker keeps the formatted version (collab_messages.html) and a plain-text one
// (collab_messages.body: thread previews, unread toasts), and the browser renders through the same rules.
// Everything here is plain string code (no DOM), so it runs in the Worker, in the browser and in Node tests.
//   - sanitizeChatHtml: a strict allowlist (b/strong, i/em, u, s/strike/del, code, pre, blockquote,
//     ul/ol/li, p/div/br, and a with an http(s) or mailto href, no other attribute). An unknown tag is
//     unwrapped to its text; script, style and the like go with everything inside them. The markup is
//     rebuilt from scratch, so nothing the sender wrote reaches the page as it was written.
//   - chatHtmlToText: the plain-text version; chatDisplayHtml: what a bubble shows (an old plain-text
//     message keeps its line breaks, and web addresses become links that open in a new tab).
//   - attachments: the limits, which files show in the conversation and how the worker serves them.
import { z } from 'zod';
/** files in one message. */
export const CHAT_MAX_FILES = 10;
/** the largest file the worker stores (photos are scaled down in the browser first). */
export const CHAT_MAX_FILE_BYTES = 15 * 1024 * 1024;
/** the longest message, as plain text. */
export const CHAT_MAX_TEXT = 5000;
/** the longest formatted message the worker takes. */
export const CHAT_MAX_HTML = 30000;
/** a photo is scaled down in the browser to this long edge before it goes up... */
export const CHAT_IMAGE_MAX_EDGE = 2400;
/** ...as a JPEG of this quality. */
export const CHAT_IMAGE_QUALITY = 0.85;
/** a photo within the long edge is still re-encoded when it is bigger than this. */
export const CHAT_IMAGE_REENCODE_BYTES = 1.5 * 1024 * 1024;
/** an upload that never made it into a message is removed after this long. */
export const CHAT_UPLOAD_TTL_MS = 24 * 60 * 60 * 1000;
/** uploads one person can have waiting to be sent at a time. */
export const CHAT_MAX_PENDING_UPLOADS = 40;
/** the quick reactions. */
export const CHAT_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '✅'];
const ALLOWED = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'p', 'div', 'br', 'a']);
// elements whose whole content goes with them (whatever is inside never shows as text either)
const DROP_CONTENT = new Set(['script', 'style', 'template', 'iframe', 'object', 'applet', 'noscript', 'noembed', 'noframes', 'xmp', 'textarea', 'title', 'svg', 'math', 'plaintext']);
// raw text / RCDATA elements: what's inside is not markup and runs to the matching end tag, as in a browser
const RAW_TEXT = new Set(['script', 'style', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript', 'textarea', 'title']);
// foreign elements honour "/>" (an empty <svg/>), HTML ones ignore it
const FOREIGN = new Set(['svg', 'math']);
// the blocks whose start ends an open <p>
const P_CLOSERS = new Set(['p', 'div', 'pre', 'blockquote', 'ul', 'ol', 'li']);
const MAX_DEPTH = 24;
const isWs = (c) => c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === '\f';
const isAlpha = (c) => !!c && ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z'));
/** the tag at s[j] (its name's first letter): name, attributes and where it ends; null when the input
 *  ends inside it (a browser drops such a tag too). Attributes are read the way the HTML tokenizer does,
 *  so a quoted ">" never ends the tag early. */
function readTag(s, j) {
    const n = s.length;
    let k = j;
    while (k < n && !isWs(s[k]) && s[k] !== '/' && s[k] !== '>')
        k++;
    const name = s.slice(j, k).toLowerCase();
    const attrs = [];
    let self = false;
    while (k < n) {
        const ch = s[k];
        if (isWs(ch)) {
            k++;
            continue;
        }
        if (ch === '>')
            return { name, attrs, self, end: k + 1 };
        if (ch === '/') {
            self = s[k + 1] === '>';
            k++;
            continue;
        }
        const a = k++; // the first character always belongs to the name, even a stray "="
        while (k < n && !isWs(s[k]) && s[k] !== '/' && s[k] !== '>' && s[k] !== '=')
            k++;
        const attr = s.slice(a, k).toLowerCase();
        while (k < n && isWs(s[k]))
            k++;
        let value = '';
        if (s[k] === '=') {
            k++;
            while (k < n && isWs(s[k]))
                k++;
            const q = s[k];
            if (q === '"' || q === "'") {
                const close = s.indexOf(q, k + 1);
                if (close < 0)
                    return null;
                value = s.slice(k + 1, close);
                k = close + 1;
            }
            else {
                const v0 = k;
                while (k < n && !isWs(s[k]) && s[k] !== '>')
                    k++;
                value = s.slice(v0, k);
            }
        }
        attrs.push([attr, value]);
    }
    return null;
}
/** where the raw text of <name> ends: the next "</name" followed by a space, "/" or ">" (any case). */
function rawTextEnd(s, from, name) {
    const re = new RegExp(`</${name}(?=[\\t\\n\\f\\r />]|$)`, 'gi');
    re.lastIndex = from;
    const m = re.exec(s);
    return m ? m.index : s.length;
}
/** after "<!--" at j: where the comment ends ("-->" or "--!>", and the empty "<!-->" / "<!--->"). */
function commentEnd(s, j) {
    if (s.startsWith('>', j))
        return j + 1;
    if (s.startsWith('->', j))
        return j + 2;
    const a = s.indexOf('-->', j);
    const b = s.indexOf('--!>', j);
    if (a < 0 && b < 0)
        return s.length;
    return b < 0 || (a >= 0 && a < b) ? a + 3 : b + 4;
}
/** split markup into text, start and end tags. Comments, doctypes and processing instructions are
 *  dropped, and so is the raw text inside script/style/textarea/title and the like. One pass, no
 *  backtracking: a long or hostile message costs time in proportion to its length. */
function tokenize(s) {
    const out = [];
    const n = s.length;
    let text = '';
    const flush = () => { if (text) {
        out.push({ t: 'text', v: text });
        text = '';
    } };
    let i = 0;
    while (i < n) {
        const lt = s.indexOf('<', i);
        if (lt < 0) {
            text += s.slice(i);
            break;
        }
        text += s.slice(i, lt);
        i = lt;
        const c = s[i + 1];
        if (s.startsWith('<!--', i)) {
            flush();
            i = commentEnd(s, i + 4);
        }
        else if (c === '!' || c === '?') {
            flush();
            const gt = s.indexOf('>', i + 2);
            i = gt < 0 ? n : gt + 1;
        }
        else if (c === '/') {
            const c2 = s[i + 2];
            if (isAlpha(c2)) {
                const tag = readTag(s, i + 2);
                if (!tag)
                    break;
                flush();
                out.push({ t: 'close', name: tag.name });
                i = tag.end;
            }
            else if (c2 === '>') {
                i += 3; // "</>" is nothing at all
            }
            else {
                flush();
                const gt = s.indexOf('>', i + 2);
                i = gt < 0 ? n : gt + 1; // "</ x>" and the like: a bogus comment
            }
        }
        else if (isAlpha(c)) {
            const tag = readTag(s, i + 1);
            if (!tag)
                break;
            flush();
            out.push({ t: 'open', name: tag.name, attrs: tag.attrs, self: tag.self });
            i = tag.end;
            if (RAW_TEXT.has(tag.name))
                i = rawTextEnd(s, i, tag.name);
            else if (tag.name === 'plaintext')
                i = n;
        }
        else {
            text += '<'; // "a < b", "<3"
            i += 1;
        }
    }
    flush();
    return out;
}
const NAMED = {
    amp: '&', AMP: '&', lt: '<', LT: '<', gt: '>', GT: '>', quot: '"', QUOT: '"', apos: "'", nbsp: '\u00a0',
    ensp: '\u2002', emsp: '\u2003', thinsp: '\u2009', zwnj: '\u200c', zwj: '\u200d', shy: '\u00ad',
    ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', sbquo: '‚', ldquo: '“', rdquo: '”', bdquo: '„', hellip: '…',
    bull: '•', middot: '·', copy: '©', reg: '®', trade: '™', euro: '€', pound: '£', yen: '¥', cent: '¢', deg: '°',
    plusmn: '±', times: '×', divide: '÷', frac12: '½', frac14: '¼', frac34: '¾', sup2: '²', sup3: '³',
    laquo: '«', raquo: '»', larr: '←', rarr: '→', uarr: '↑', darr: '↓', harr: '↔', check: '✓', sect: '§',
    para: '¶', micro: 'µ', iexcl: '¡', iquest: '¿', prime: '′', Prime: '″', dagger: '†', Dagger: '‡', permil: '‰',
    Tab: '\t', NewLine: '\n', colon: ':', semi: ';', comma: ',', period: '.', excl: '!', quest: '?', num: '#',
    dollar: '$', percnt: '%', ast: '*', plus: '+', equals: '=', commat: '@', sol: '/', bsol: '\\', lpar: '(',
    rpar: ')', lsqb: '[', rsqb: ']', lcub: '{', rcub: '}', verbar: '|', lowbar: '_', grave: '`', Hat: '^',
};
// how browsers read &#128; - &#159; (Windows-1252), the codes Word likes to leave behind
const WIN1252 = '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f\u0090‘’“”•–—˜™š›œ\u009džŸ';
/** character references to characters: named ones the app is likely to meet, and any numeric one. An
 *  unknown name stays as written. (Whatever comes out is escaped again on the way out.) */
export function decodeEntities(s) {
    if (!s.includes('&'))
        return s;
    return s.replace(/&(?:#(\d+)|#[xX]([0-9a-fA-F]+)|([a-zA-Z][a-zA-Z0-9]*));?/g, (m, dec, hex, name) => {
        if (name !== undefined)
            return m.endsWith(';') && NAMED[name] !== undefined ? NAMED[name] : m;
        const code = dec !== undefined ? (dec.length > 8 ? Infinity : parseInt(dec, 10)) : hex.length > 7 ? Infinity : parseInt(hex, 16);
        if (!code || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff))
            return '\ufffd';
        if (code >= 0x80 && code <= 0x9f)
            return WIN1252[code - 0x80];
        return String.fromCodePoint(code);
    });
}
const escapeText = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (s) => escapeText(s).replace(/"/g, '&quot;');
// control characters (bar tab and newline), zero-width spaces and byte-order marks never belong in a message
// eslint-disable-next-line no-control-regex
const JUNK = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u200b\ufeff]/g;
const MAX_URL = 2048;
/** a link target the chat keeps: an http(s) or mailto address, decoded and tidied the way a browser reads
 *  it (tabs and newlines inside dropped, spaces and controls at the ends trimmed). Anything else
 *  (javascript:, data:, vbscript:, a relative path …) is null. */
export function safeChatHref(raw) {
    let u = decodeEntities(String(raw ?? ''));
    if (u.length > MAX_URL)
        return null;
    u = u.replace(/[\t\n\r]/g, '');
    let a = 0;
    let b = u.length;
    while (a < b && u.charCodeAt(a) <= 0x20)
        a++;
    while (b > a && u.charCodeAt(b - 1) <= 0x20)
        b--;
    u = u.slice(a, b);
    if (!/^(?:https?:\/\/[^\s/?#\\]|mailto:\S)/i.test(u))
        return null;
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(u))
        return null;
    return u.replace(/ /g, '%20');
}
// a web address in running text: http(s)://… or www.…
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;
const count = (s, c) => s.split(c).length - 1;
/** a web address without the punctuation that ends the sentence around it ("see https://x.com/a.") or a
 *  bracket that closes around it ("(https://x.com)"). */
function trimUrlTail(u) {
    for (;;) {
        const c = u.charAt(u.length - 1);
        if ('.,;:!?*'.includes(c) && c)
            u = u.slice(0, -1);
        else if (c === ')' && count(u, '(') < count(u, ')'))
            u = u.slice(0, -1);
        else if (c === ']' && count(u, '[') < count(u, ']'))
            u = u.slice(0, -1);
        else
            return u;
    }
}
const LINK_ATTRS = ' target="_blank" rel="noopener noreferrer"';
/** escaped text with its web addresses made into links (that open in a new tab). */
function linkifyText(text) {
    let out = '';
    let last = 0;
    for (const m of text.matchAll(URL_RE)) {
        if (m[0].length > MAX_URL)
            continue;
        const url = trimUrlTail(m[0]);
        if (!/^(?:https?:\/\/|www\.)./i.test(url))
            continue;
        const href = safeChatHref(/^www\./i.test(url) ? `https://${url}` : url);
        if (!href)
            continue;
        const at = m.index ?? 0;
        out += `${escapeText(text.slice(last, at))}<a href="${escapeAttr(href)}"${LINK_ATTRS}>${escapeText(url)}</a>`;
        last = at + url.length;
    }
    return out + escapeText(text.slice(last));
}
/** the formatted message with only the chat's own markup left (see the top of this file). The result is
 *  well-formed: every tag is closed and nested, text is escaped, a link carries nothing but its href. */
export function sanitizeChatHtml(html, opts = {}) {
    const toks = tokenize(String(html ?? '').replace(/\r\n?/g, '\n'));
    const stack = [];
    let out = '';
    let drop = null;
    const closeTo = (at) => {
        let s = '';
        while (stack.length > at)
            s += `</${stack.pop()}>`;
        return s;
    };
    for (const tok of toks) {
        if (drop) {
            // inside a dropped element: only its own start and end tags count, to find where it ends
            if (tok.t === 'open' && tok.name === drop.name && !(tok.self && FOREIGN.has(tok.name)))
                drop.depth++;
            else if (tok.t === 'close' && tok.name === drop.name && --drop.depth === 0)
                drop = null;
            continue;
        }
        if (tok.t === 'text') {
            const v = decodeEntities(tok.v).replace(JUNK, '');
            const plain = !opts.display || stack.includes('a') || stack.includes('code') || stack.includes('pre');
            out += plain ? escapeText(v) : linkifyText(v);
            continue;
        }
        if (tok.t === 'open') {
            if (DROP_CONTENT.has(tok.name)) {
                if (!(tok.self && FOREIGN.has(tok.name)))
                    drop = { name: tok.name, depth: 1 };
                continue;
            }
            if (!ALLOWED.has(tok.name))
                continue; // unwrapped: its text stays
            if (tok.name === 'br') {
                out += '<br>';
                continue;
            }
            // as in a browser: a block ends an open paragraph, and a list item ends the open item of its list
            const p = stack.lastIndexOf('p');
            if (p >= 0 && P_CLOSERS.has(tok.name))
                out += closeTo(p);
            if (tok.name === 'li') {
                const li = stack.lastIndexOf('li');
                if (li >= 0 && li > Math.max(stack.lastIndexOf('ul'), stack.lastIndexOf('ol')))
                    out += closeTo(li);
            }
            if (stack.length >= MAX_DEPTH)
                continue;
            if (tok.name === 'a') {
                const href = tok.attrs.find(([k]) => k === 'href');
                const safe = href ? safeChatHref(href[1]) : null;
                if (!safe)
                    continue; // a link we can't keep is just its text
                const open = stack.lastIndexOf('a');
                if (open >= 0)
                    out += closeTo(open); // a link inside a link ends the first one, as in a browser
                out += `<a href="${escapeAttr(safe)}"${opts.display ? LINK_ATTRS : ''}>`;
                stack.push('a');
                continue;
            }
            out += `<${tok.name}>`;
            stack.push(tok.name);
            continue;
        }
        if (tok.name === 'br' || !ALLOWED.has(tok.name))
            continue;
        const at = stack.lastIndexOf(tok.name);
        if (at >= 0)
            out += closeTo(at);
    }
    return out + closeTo(0);
}
// empty lines a contentEditable leaves at the ends of a message
const EDGES = ['<br>', '<div></div>', '<p></p>', '<div><br></div>', '<p><br></p>'];
const isSpace = (c) => !!c && /\s/.test(c);
/** sanitized markup without the blank lines and spaces at its start and end. */
export function trimChatHtml(html) {
    let a = 0;
    let b = html.length;
    for (let moved = true; moved && a < b;) {
        moved = false;
        while (a < b && isSpace(html[a])) {
            a++;
            moved = true;
        }
        while (b > a && isSpace(html[b - 1])) {
            b--;
            moved = true;
        }
        for (const t of EDGES) {
            if (b - a >= t.length && html.startsWith(t, a)) {
                a += t.length;
                moved = true;
            }
            if (b - a >= t.length && html.endsWith(t, b)) {
                b -= t.length;
                moved = true;
            }
        }
    }
    return html.slice(a, b);
}
const BLOCK = new Set(['p', 'div', 'pre', 'blockquote', 'ul', 'ol', 'li']);
/** the plain-text version of a formatted message: line breaks where the lines were, list items with a
 *  bullet or their number, the text of a link (not its address). */
export function chatHtmlToText(html) {
    let out = '';
    let pre = 0;
    const lists = [];
    // a list item's bullet waits for its text, so an empty item (a list started and left) adds nothing
    let bullet = '';
    const newline = () => { if (out && !out.endsWith('\n'))
        out += '\n'; };
    for (const tok of tokenize(sanitizeChatHtml(html))) {
        if (tok.t === 'text') {
            let v = decodeEntities(tok.v);
            if (!pre) {
                v = v.replace(/[\t\n\f\r ]+/g, ' ');
                if (!out || out.endsWith('\n'))
                    v = v.replace(/^ /, '');
            }
            v = v.replace(/\u00a0/g, ' ');
            if (bullet && !v.trim())
                continue;
            out += bullet + v;
            bullet = '';
        }
        else if (tok.t === 'open') {
            if (tok.name === 'br') {
                out += '\n';
                continue;
            }
            if (BLOCK.has(tok.name))
                newline();
            if (tok.name === 'pre')
                pre++;
            else if (tok.name === 'ul' || tok.name === 'ol') {
                lists.push({ ordered: tok.name === 'ol', n: 0 });
                bullet = '';
            }
            else if (tok.name === 'li') {
                const l = lists[lists.length - 1];
                bullet = '  '.repeat(Math.max(0, lists.length - 1)) + (l?.ordered ? `${++l.n}. ` : '• ');
            }
        }
        else {
            if (tok.name === 'pre')
                pre = Math.max(0, pre - 1);
            else if (tok.name === 'ul' || tok.name === 'ol')
                lists.pop();
            else if (tok.name === 'li')
                bullet = '';
            if (BLOCK.has(tok.name))
                newline();
        }
    }
    return out.split('\n').map((l) => l.trimEnd()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
/** a plain-text message as markup: escaped, with its line breaks. */
export function chatTextToHtml(text) {
    return escapeText(String(text ?? '').replace(/\r\n?/g, '\n')).replace(/\n/g, '<br>');
}
/** what a bubble shows: the formatted version through the same rules (links open in a new tab, web
 *  addresses become links), or an old plain-text message with its line breaks kept. */
export function chatDisplayHtml(html, body) {
    return sanitizeChatHtml(html ? html : chatTextToHtml(body), { display: true });
}
/** what the worker stores for a message: the cleaned markup (null when there is none) and its plain text.
 *  A message sent without markup (the plain `body` older app versions send) stays plain text. */
export function chatContent(input) {
    if (input.html != null && input.html.trim()) {
        const html = trimChatHtml(sanitizeChatHtml(input.html));
        const text = chatHtmlToText(html);
        return text ? { html, text } : { html: null, text: '' };
    }
    return { html: null, text: String(input.body ?? '').replace(/\r\n?/g, '\n').replace(JUNK, '').trim() };
}
// ── files ────────────────────────────────────────────────────────────────────────
/** images shown in the conversation (and served inline). Not SVG, which is a document that can run
 *  script; not HEIC, which most browsers can't show (it goes as a file). */
export const CHAT_INLINE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const baseType = (t) => String(t ?? '').split(';')[0].trim().toLowerCase();
/** how the app treats a file: an image shown in the conversation, a PDF opened in the browser, or a
 *  file to download. */
export function chatFileKind(type) {
    const t = baseType(type);
    if (CHAT_INLINE_IMAGE_TYPES.includes(t))
        return 'image';
    return t === 'application/pdf' ? 'pdf' : 'file';
}
/** a photo in the thread-list sense ("Photo", "3 photos"): any image but an SVG drawing. */
export const isChatPhotoType = (type) => {
    const t = baseType(type);
    return t.startsWith('image/') && !t.includes('svg');
};
const EXT_TYPES = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', avif: 'image/avif',
    heic: 'image/heic', heif: 'image/heif', pdf: 'application/pdf', txt: 'text/plain', csv: 'text/csv',
    doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    zip: 'application/zip', mp4: 'video/mp4', mov: 'video/quicktime', dwg: 'image/vnd.dwg', dxf: 'image/vnd.dxf',
};
const MIME_RE = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,63}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/;
/** the type the worker stores for an upload: the one the browser declared, tidied, or one from the file
 *  name's extension, else application/octet-stream. */
export function chatMimeType(declared, filename) {
    const t = baseType(declared);
    const ext = /\.([a-z0-9]{1,8})$/i.exec(filename)?.[1]?.toLowerCase() ?? '';
    if (t && t !== 'application/octet-stream' && MIME_RE.test(t))
        return t === 'image/jpg' ? 'image/jpeg' : t;
    return EXT_TYPES[ext] ?? 'application/octet-stream';
}
/** a file's name as the app keeps it: no folders, no control characters, at most 180 characters. */
export function chatFileName(name) {
    const base = String(name ?? '').replace(JUNK, '').split(/[\\/]/).pop() ?? '';
    const clean = base.replace(/[\u0000-\u001f]/g, '').trim(); // eslint-disable-line no-control-regex
    return (clean.length > 180 ? clean.slice(0, 180) : clean) || 'file';
}
// what a file to download may be served as, besides the photos and PDFs the app shows: documents, sheets,
// slides, archives, drawings and media. Anything else (a page, a drawing that is really a page, a script,
// a style sheet, a type nobody knows) goes out as application/octet-stream, which a browser never runs,
// renders or applies, whatever the page around it asks.
const DOWNLOAD_TYPES = new Set([
    ...Object.values(EXT_TYPES),
    'text/plain', 'text/csv', 'application/rtf', 'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.oasis.opendocument.presentation',
    'application/x-zip-compressed', 'application/x-7z-compressed', 'application/vnd.rar', 'application/x-rar-compressed', 'application/gzip',
    'image/tiff', 'image/bmp', 'video/webm', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav',
]);
const rfc5987 = (s) => encodeURIComponent(s).replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
/** the response headers for a stored file. Images and PDFs open in the browser; anything else is a
 *  download, under its own type only when that is a known document or media type (DOWNLOAD_TYPES), else as
 *  application/octet-stream. nosniff keeps the browser to that type (so no file can be loaded as a script
 *  or a style sheet), and the sandbox leaves an opened file powerless on the app's origin.
 *  A PDF opened in the browser goes without one: any policy stops Chrome's PDF viewer from drawing it, and
 *  a PDF's own scripts only ever run inside the viewer. */
export function chatFileHeaders(f, download = false) {
    const kind = chatFileKind(f.type);
    const inline = !download && kind !== 'file';
    const stored = baseType(f.type);
    const type = kind !== 'file' || DOWNLOAD_TYPES.has(stored) ? stored : 'application/octet-stream';
    const name = chatFileName(f.name);
    const ascii = name.replace(/[^\x20-\x7e]/g, '_').replace(/["\\;]/g, '_');
    const headers = {
        'content-type': type,
        'content-disposition': `${inline ? 'inline' : 'attachment'}; filename="${ascii}"; filename*=UTF-8''${rfc5987(name)}`,
        'x-content-type-options': 'nosniff',
        'cache-control': 'private, max-age=31536000, immutable',
    };
    if (!(inline && kind === 'pdf'))
        headers['content-security-policy'] = "default-src 'none'; img-src 'self' data:; style-src 'unsafe-inline'; sandbox";
    return headers;
}
/** "Photo", "3 photos", the file's name, or "2 files": the preview of a message without text. */
export function chatAttachmentSummary(files) {
    if (!files.length)
        return '';
    const photos = files.filter((f) => isChatPhotoType(f.type)).length;
    if (photos === files.length)
        return photos === 1 ? 'Photo' : `${photos} photos`;
    return files.length === 1 ? files[0].name || 'File' : `${files.length} files`;
}
/** the one-line preview of a message for the thread list. */
export function chatPreview(m) {
    if (m.deleted)
        return 'Message deleted';
    const text = String(m.body ?? '').replace(/\s+/g, ' ').trim();
    if (text)
        return text.length > 160 ? `${text.slice(0, 159)}…` : text;
    return chatAttachmentSummary(m.files ?? []);
}
/** a size for people: "860 KB", "2.4 MB". */
export function formatFileSize(bytes) {
    if (!(bytes > 0))
        return '0 KB';
    if (bytes < 1024 * 1024)
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    const mb = bytes / 1024 / 1024;
    return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
/** reaction rows grouped per emoji, in the quick set's order (so the chips never jump about between polls),
 *  with my own marked and the names for the hover ("You" first, for me). */
export function groupChatReactions(rows, me, nameOf) {
    const by = new Map();
    const self = me.toLowerCase();
    for (const r of rows) {
        const g = by.get(r.emoji) ?? { emoji: r.emoji, count: 0, mine: false, names: [] };
        g.count += 1;
        if (r.user.toLowerCase() === self) {
            g.mine = true;
            g.names.unshift('You');
        }
        else {
            g.names.push(nameOf(r.user));
        }
        by.set(r.emoji, g);
    }
    const order = (e) => {
        const i = CHAT_REACTIONS.indexOf(e);
        return i < 0 ? CHAT_REACTIONS.length : i;
    };
    return [...by.values()].sort((a, b) => order(a.emoji) - order(b.emoji));
}
// ── requests ─────────────────────────────────────────────────────────────────────
const ThreadId = z.string().min(1).max(120);
const MessageId = z.number().int().positive();
const AttachmentId = z.string().min(1).max(64);
/** send a message: the formatted text (and/or plain body), plus files uploaded to this thread first. */
export const ChatSendSchema = z.object({
    thread: ThreadId,
    html: z.string().max(CHAT_MAX_HTML).optional(),
    body: z.string().max(CHAT_MAX_TEXT).optional(),
    attachments: z.array(AttachmentId).max(CHAT_MAX_FILES).default([]),
});
/** change the text of my own message. */
export const ChatEditSchema = z.object({
    id: MessageId,
    html: z.string().max(CHAT_MAX_HTML).optional(),
    body: z.string().max(CHAT_MAX_TEXT).optional(),
});
export const ChatMessageRefSchema = z.object({ id: MessageId });
/** add (on) or take back (off) my reaction. */
export const ChatReactSchema = z.object({ id: MessageId, emoji: z.enum(CHAT_REACTIONS), on: z.boolean() });
/** uploads taken out of the composer before sending: one (a chip removed), or all a page had waiting as it
 *  closes. */
export const ChatDiscardSchema = z
    .object({ id: AttachmentId.optional(), ids: z.array(AttachmentId).max(CHAT_MAX_PENDING_UPLOADS).optional() })
    .refine((v) => !!v.id || !!v.ids?.length, { message: 'id required' });
//# sourceMappingURL=chat.js.map